import type { ReactNode } from 'react';
import { useTranslations } from 'use-intl';
import {
  Body,
  Button,
  Caption,
  Card,
  I,
  Icon,
  Pill,
  Row,
  Stack,
} from '@leanin/ui';
import type { PostMediaView } from '@leanin/shared';

const THUMB_WIDTH_PX = 56;
const THUMB_HEIGHT_PX = 72;

type Props = {
  item: PostMediaView;
};

// Inner content of the document card — used by both the read-only renderer
// and the composer's editable variant so the two stay WYSIWYG-aligned.
// `trailing` is rendered after the ext-badge and is the canonical slot for
// editable-mode controls (Remove, Retry-on-error). Mirrors the
// PostEntityCard / PostEntityCardBody pattern, but for a single inline
// control rather than a footer action row — media cards only ever have
// one or two icon-sized actions, not the Swap+Remove pair that earns
// PostEntityCard.Editable its footer.
function PostMediaDocumentCardBody({
  item,
  trailing,
}: {
  item: PostMediaView;
  trailing?: ReactNode;
}) {
  const t = useTranslations('Feed');
  return (
    <Row gap="md" align="center" p="md">
      <DocumentThumbnail item={item} />
      <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
        <Body
          size="md"
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {filenameFor(item, t)}
        </Body>
        <Caption>{metaLineFor(item, t)}</Caption>
      </Stack>
      <Pill size="xs" tone="neutral">
        {extensionLabelFor(item)}
      </Pill>
      {trailing}
    </Row>
  );
}

export function PostMediaDocumentCard({ item }: Props) {
  return (
    <Card pad="flush" style={{ overflow: 'hidden' }}>
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'block',
          color: 'inherit',
          textDecoration: 'none',
        }}
      >
        <PostMediaDocumentCardBody item={item} />
      </a>
    </Card>
  );
}

// Editable variant — composer affordances (remove / retry-on-error) layered
// onto the same Body, plus a dim upload-state scrim and an inline error
// message below. The Body MUST stay identical to the read-only card so the
// composer preview matches what gets rendered after submit.
type EditableProps = {
  item: PostMediaView & {
    uploading?: boolean;
    uploadPct?: number | null;
    uploadError?: string | null;
  };
  onRemove?: () => void;
  onRetry?: () => void;
};

function PostMediaDocumentCardEditable({
  item,
  onRemove,
  onRetry,
}: EditableProps) {
  const t = useTranslations('Feed');
  const uploading = item.uploading === true;
  const errorMessage =
    item.uploadError !== null && item.uploadError !== undefined
      ? item.uploadError
      : null;

  const trailing = (
    <>
      {errorMessage !== null && onRetry !== undefined ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {t('media.retry')}
        </Button>
      ) : null}
      {onRemove !== undefined ? (
        <Button
          variant="ghost"
          size="sm"
          icon={I.x}
          aria-label={t('media.removeAttachment')}
          onClick={onRemove}
        />
      ) : null}
    </>
  );

  return (
    <Stack gap="sm">
      <Card pad="flush" style={{ overflow: 'hidden', position: 'relative' }}>
        <PostMediaDocumentCardBody item={item} trailing={trailing} />
        {uploading ? (
          <div
            aria-live="polite"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.7)',
            }}
          >
            <Body size="sm" color="mute">
              {item.uploadPct !== null && item.uploadPct !== undefined
                ? t('media.uploadingPct', { pct: item.uploadPct })
                : t('media.uploading')}
            </Body>
          </div>
        ) : null}
      </Card>
      {errorMessage !== null ? (
        <Body color="destructive">{errorMessage}</Body>
      ) : null}
    </Stack>
  );
}

PostMediaDocumentCard.Editable = PostMediaDocumentCardEditable;

function DocumentThumbnail({ item }: { item: PostMediaView }) {
  if (item.thumbUrl !== null) {
    return (
      <img
        src={item.thumbUrl}
        alt=""
        role="presentation"
        style={{
          width: THUMB_WIDTH_PX,
          height: THUMB_HEIGHT_PX,
          objectFit: 'cover',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--muted)',
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <Row
      align="center"
      justify="center"
      style={{
        width: THUMB_WIDTH_PX,
        height: THUMB_HEIGHT_PX,
        borderRadius: 'var(--radius-sm)',
        background: 'var(--muted)',
        color: 'var(--mute)',
        flexShrink: 0,
      }}
    >
      <Icon size="lg">{I.book}</Icon>
    </Row>
  );
}

function filenameFor(
  item: PostMediaView,
  t: ReturnType<typeof useTranslations<'Feed'>>,
): string {
  if (item.altText !== null && item.altText.length > 0) return item.altText;
  return item.kind === 'doc'
    ? t('media.documentDocx')
    : t('media.documentPdf');
}

function extensionLabelFor(item: PostMediaView): string {
  return item.kind === 'doc' ? 'DOCX' : 'PDF';
}

function metaLineFor(
  item: PostMediaView,
  t: ReturnType<typeof useTranslations<'Feed'>>,
): string {
  const parts: string[] = [];
  if (item.pageCount !== null) {
    parts.push(t('media.pageCount', { count: item.pageCount }));
  }
  parts.push(formatBytes(item.sizeBytes));
  return parts.join(' · ');
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
